import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const TABS = ["Pending", "Approved", "Rejected", "All"];
const TAB_STATUS = { Pending: "pending", Approved: "approved", Rejected: "rejected", All: "" };
const STATUS_TONE = { pending: "warning", approved: "success", rejected: "danger" };

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  label: { fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.4px" },
  valueRow: { display: "flex", alignItems: "center", gap: "10px", margin: "6px 0 20px" },
  arrow: { color: "#94a3b8" }
};

class BiodataAppeals extends Component {
  state = {
    appeals: [], loading: true, tab: 0,
    selected: null, detailOpen: false,
    remark: "", saving: false, error: null
  };

  componentDidMount() { this.fetchAppeals(); }

  fetchAppeals = async () => {
    this.setState({ loading: true });
    try {
      const status = TAB_STATUS[TABS[this.state.tab]];
      const res = await api.get("/biodata-appeals", { params: { limit: 100, ...(status ? { status } : {}) } });
      const d = res.data.data || res.data;
      this.setState({ appeals: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handleTabChange = (t) => this.setState({ tab: t }, this.fetchAppeals);

  openDetail = async (appeal) => {
    this.setState({ detailOpen: true, selected: appeal, remark: "", error: null });
    try {
      const res = await api.get(`/biodata-appeals/${appeal.id}`);
      this.setState({ selected: res.data.data || res.data });
    } catch (e) { /* use cached row */ }
  };

  handleApprove = async () => {
    const { selected, remark } = this.state;
    this.setState({ saving: true, error: null });
    try {
      await api.patch(`/biodata-appeals/${selected.id}/approve`, { remark });
      this.setState({ detailOpen: false, saving: false });
      this.fetchAppeals();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Approval failed.", saving: false });
    }
  };

  handleReject = async () => {
    const { selected, remark } = this.state;
    if (!remark.trim()) { this.setState({ error: "A remark is required to reject an appeal." }); return; }
    this.setState({ saving: true, error: null });
    try {
      await api.patch(`/biodata-appeals/${selected.id}/reject`, { remark: remark.trim() });
      this.setState({ detailOpen: false, saving: false });
      this.fetchAppeals();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Rejection failed.", saving: false });
    }
  };

  render() {
    const { classes, history } = this.props;
    const { appeals, loading, tab, selected, detailOpen, remark, saving, error } = this.state;

    const columns = [
      {
        id: "student",
        header: "Student",
        cell: info => {
          const s = info.row.original.student || {};
          return s.id ? (
            <span style={{ color: "#1a237e", fontWeight: 700, cursor: "pointer" }} onClick={() => history.push(`/bwillis/students/${s.id}`)}>
              {s.firstName} {s.lastName}
            </span>
          ) : "—";
        }
      },
      { id: "studentNo", header: "Std No", accessorFn: a => a.student?.studentNo || "—", meta: { variant: "mono" } },
      { id: "field", header: "Field", accessorFn: a => a.field },
      { id: "proposed", header: "Requested Value", accessorFn: a => a.proposedValue },
      {
        id: "status",
        header: "Status",
        cell: info => <Pill tone={STATUS_TONE[info.row.original.status] || "muted"}>{info.row.original.status}</Pill>
      },
      { id: "date", header: "Submitted", accessorFn: a => (a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "—") },
      {
        id: "actions",
        header: "",
        cell: info => <Button size="sm" color="info" simple onClick={() => this.openDetail(info.row.original)}>View</Button>
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar tabs={TABS} activeTab={tab} onTabChange={this.handleTabChange} actions={[]} />
          <DataTable
            title="Biodata Correction Requests"
            subtitle="Review and action student-submitted appeals to their biodata"
            columns={columns}
            data={appeals}
            isLoading={loading}
            emptyState="No appeals found."
          />
        </GridItem>

        <Dialog open={detailOpen} onClose={() => this.setState({ detailOpen: false })} maxWidth="xs" fullWidth>
          <DialogHeader
            title="Appeal Detail"
            subtitle={selected ? `${selected.student?.firstName || ""} ${selected.student?.lastName || ""} — ${selected.field}` : ""}
            icon="fact_check"
            color="info"
            onClose={() => this.setState({ detailOpen: false })}
          />
          <DialogContent>
            {error && <p className={classes.errorMsg} style={{ marginBottom: "12px" }}>{error}</p>}
            {selected && (
              <>
                <div className={classes.label}>Change Requested</div>
                <div className={classes.valueRow}>
                  <span>{selected.currentValue || "—"}</span>
                  <Icon className={classes.arrow}>arrow_forward</Icon>
                  <span style={{ fontWeight: 700, color: "#1a237e" }}>{selected.proposedValue}</span>
                </div>
                {selected.reason && (
                  <>
                    <div className={classes.label}>Student's Reason</div>
                    <p style={{ marginTop: "4px" }}>{selected.reason}</p>
                  </>
                )}

                {selected.status === "pending" ? (
                  <>
                    <CustomInput
                      labelText="Remark (required to reject)"
                      id="appealRemark"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{ value: remark, onChange: e => this.setState({ remark: e.target.value }), multiline: true, rows: 2 }}
                    />
                    <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                      <Button color="success" onClick={this.handleApprove} disabled={saving} style={{ flex: 1 }}>
                        {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Approve"}
                      </Button>
                      <Button color="danger" onClick={this.handleReject} disabled={saving} style={{ flex: 1 }}>
                        Reject
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={classes.label}>Admin Remark</div>
                    <p style={{ marginTop: "4px" }}>{selected.adminRemark || "—"}</p>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ detailOpen: false })} disabled={saving}>Close</Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

BiodataAppeals.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(BiodataAppeals));
